import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { fetchStats } from "../lib/api";
import { STATS_QUOTE_SECTION } from "../data/home-data";

function Counter({ to, label, suffix = "+" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const dur = 1800;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.floor(eased * to));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [inView, to]);
    return (
        <div ref={ref} data-testid={`stat-${label}`}>
            <div className="font-display font-black text-6xl sm:text-7xl text-white tracking-tighter">
                {n.toLocaleString()}
                {suffix}
            </div>
            <p className="text-aipeach text-sm uppercase tracking-widest mt-2 font-semibold">
                {label}
            </p>
        </div>
    );
}

export default function StatsQuote() {
    const [stats, setStats] = useState({
        students_waitlist: 2847,
        hiring_partners: 60,
        university_partners: 18,
    });
    useEffect(() => {
        fetchStats()
            .then(setStats)
            .catch(() => {});
    }, []);

    return (
        <section
            className="relative py-28 overflow-hidden grain bg-[#0A0A0A]"
            data-testid="stats-section"
        >
            <div
                className="absolute inset-0 opacity-20"
                style={{
                    background:
                        "radial-gradient(circle at 20% 30%, #0052FF, transparent 50%), radial-gradient(circle at 80% 70%, #FF6B00, transparent 50%)",
                }}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-6">
                        {STATS_QUOTE_SECTION.tag}
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-5xl text-white mb-12 leading-tight">
                        {STATS_QUOTE_SECTION.headline}
                    </h2>
                    <div className="grid grid-cols-2 gap-8">
                        <Counter
                            to={stats.students_waitlist}
                            label="Students on Waitlist"
                        />
                        <Counter
                            to={stats.hiring_partners}
                            label="Hiring Companies"
                        />
                        <Counter
                            to={stats.university_partners}
                            label="University Partners"
                        />
                        <Counter
                            to={stats.mentors || 75}
                            label="Industry Mentors"
                        />
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="relative bg-aipeach rounded-[2.5rem] p-10 sm:p-12 shadow-2xl"
                    data-testid="founder-quote-card"
                >
                    <div className="font-display text-9xl text-aiblue/30 absolute top-4 left-6 leading-none select-none">
                        "
                    </div>
                    <p className="relative pt-6 text-xl sm:text-2xl font-medium text-gray-900 leading-relaxed">
                        {STATS_QUOTE_SECTION.quote.text}
                    </p>
                    <div className="mt-8 flex items-center gap-4">
                        <img
                            src={STATS_QUOTE_SECTION.quote.img}
                            alt={STATS_QUOTE_SECTION.quote.author}
                            className="h-14 w-14 rounded-full object-cover"
                        />
                        <div>
                            <div className="font-display font-bold text-lg">
                                {STATS_QUOTE_SECTION.quote.author}
                            </div>
                            <div className="text-sm text-gray-600">
                                {STATS_QUOTE_SECTION.quote.role}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
